import { useNavigate } from 'react-router-dom';
import { Button, Card, Typography, Space, message, Alert, Grid } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { CheckCircleOutlined, CopyOutlined, GiftOutlined, HomeOutlined, UnorderedListOutlined, UserAddOutlined } from '@ant-design/icons';
import { useGuestSession } from '../../services/useGuestSession';
import { useAuth } from '../../hooks/useAuth';
import { useCycleHistory } from '../../hooks/useEngagementTickets';
import { engagementService } from '../../services/engagement.service';
import { EngagementCycleSummaryCard } from '../engagement/EngagementCycleSummaryCard';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const BRAND_GREEN = '#00984A';

// Phase 6 - US4: Submission Confirmation Component (T116)

interface SubmissionConfirmationProps {
  responseHash: string;
  surveyName: string;
}

const SubmissionConfirmation = ({ responseHash, surveyName }: SubmissionConfirmationProps) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const isParticipant = isAuthenticated && user?.role === 'PARTICIPANT';
  const guestSession = useGuestSession();
  const showLinkBanner = guestSession.isActive && !guestSession.hasSeenLinkBanner();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Distinct query key + no staleTime: this screen must always show the
  // points/tickets earned by the submission that just happened, so it can't
  // reuse the 5-minute-stale cache entry other pages (e.g. the home page)
  // keep under the shared 'participant-referral-link' key.
  const { data: engagementData } = useQuery({
    queryKey: ['submission-confirmation-current-cycle', user?.userId, responseHash],
    queryFn: () => engagementService.getCurrentCycle({ page: 1, pageSize: 10 }),
    enabled: isParticipant,
  });

  const { data: cycleHistoryData } = useCycleHistory(1, 10, { enabled: isParticipant });
  const lastResultsCycleKey = cycleHistoryData?.cycles.find((c) => c.hasResults)?.cycleKey ?? null;

  const handleCopyHash = () => {
    navigator.clipboard.writeText(responseHash);
    message.success('Hash copiado para a área de transferência!');
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Card className="text-center">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Success Icon */}
          <div>
            <CheckCircleOutlined style={{ fontSize: '72px', color: '#00984A' }} />
          </div>

          {/* Success Message */}
          <div>
            <Title level={2} style={{ marginBottom: '8px' }}>
              Respostas Enviadas com Sucesso!
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              Obrigado por participar da pesquisa "{surveyName}"
            </Text>
          </div>

          {/* Response Hash */}
          <Card
            type="inner"
            style={{ backgroundColor: '#f0faf5', borderColor: '#b7eb8f' }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Title level={5} style={{ margin: 0, color: '#00984A' }}>
                Comprovante de Participação
              </Title>
              <Text style={{ fontSize: '12px', color: '#595959' }}>
                Guarde este código hash como comprovante. Ele garante a autenticidade da sua resposta.
              </Text>
              <div style={{ padding: '12px 16px', backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '6px' }}>
                <code style={{ fontSize: '14px', color: '#262626', wordBreak: 'break-all' }}>
                  {responseHash}
                </code>
              </div>
              <Button
                icon={<CopyOutlined />}
                onClick={handleCopyHash}
              >
                Copiar Hash
              </Button>
            </Space>
          </Card>

          {/* Info Box */}
          <Card 
            type="inner" 
            style={{ textAlign: 'left' }}
          >
            <Title level={5} style={{ marginTop: 0 }}>O que acontece agora?</Title>
            <ul style={{ paddingLeft: '20px', margin: 0 }}>
              <li><Text>✓ Suas respostas foram registradas de forma anônima</Text></li>
              <li><Text>✓ Seu perfil demográfico foi capturado (sem identificação pessoal)</Text></li>
              <li><Text>✓ Você não poderá responder esta pesquisa novamente</Text></li>
              <li><Text>✓ Os resultados estarão disponíveis após o período de coleta</Text></li>
            </ul>
          </Card>

          {/* Engagement lottery ticket messaging */}
          {isAuthenticated ? (
            <Space direction="vertical" size={16} style={{ width: '100%', textAlign: 'left' }}>
              <Alert
                type="success"
                showIcon
                icon={<GiftOutlined style={{ color: BRAND_GREEN, fontSize: '20px' }} />}
                style={{ backgroundColor: '#f0faf5', borderColor: '#b7eb8f' }}
                message={
                  <Text strong style={{ color: BRAND_GREEN, fontSize: '15px' }}>
                    Você ganhou bilhetes para o ciclo de premiação!
                  </Text>
                }
                description={
                  <Text>
                    Essa participação gera bilhetes para concorrer aos prêmios do programa de engajamento da PePoll. Compartilhar pesquisas com novos usuários também gera bilhetes extras.
                  </Text>
                }
              />
              {isParticipant && (
                <EngagementCycleSummaryCard
                  cycle={engagementData?.cycle}
                  onCtaClick={() => navigate('/engagement')}
                  participantProgress={
                    engagementData?.participant
                      ? {
                          totalPoints: engagementData.participant.totalPoints,
                          eligibleForPrize: engagementData.participant.eligibleForPrize,
                          pointsToPrizeThreshold: engagementData.rankingSnapshot?.pointsToPrizeThreshold ?? 0,
                        }
                      : null
                  }
                  referralUrl={engagementData?.referralLink?.url}
                  lastResultsCycleKey={lastResultsCycleKey}
                />
              )}
            </Space>
          ) : (
            <Alert
              type="warning"
              showIcon
              icon={<GiftOutlined />}
              message="Quer concorrer aos prêmios do programa de engajamento?"
              description={
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Text>
                    Sem uma conta PePoll logada, sua participação não gera bilhetes para o sorteio de prêmios. Crie sua conta gratuitamente para começar a receber bilhetes — compartilhar pesquisas com novos usuários também gera bilhetes extras.
                  </Text>
                  <Button size="small" type="primary" block={isMobile} onClick={() => navigate('/signup')}>
                    Criar Conta
                  </Button>
                </Space>
              }
            />
          )}

          {/* Guest account link banner */}
          {showLinkBanner && (
            <Alert
              type="info"
              showIcon
              icon={<UserAddOutlined />}
              message="Quer acompanhar seu histórico de participações?"
              description={
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Text>Vincule sua conta PePoll para acessar seu histórico sempre que entrar.</Text>
                  <Button
                    size="small"
                    type="primary"
                    block={isMobile}
                    onClick={() => {
                      guestSession.markLinkBannerShown();
                      navigate('/guest/account-link');
                    }}
                  >
                    Vincular conta
                  </Button>
                </Space>
              }
            />
          )}

          {/* Actions */}
          <Space wrap>
            <Button
              type="primary"
              size="large"
              icon={<UnorderedListOutlined />}
              onClick={() => navigate('/surveys')}
            >
              Ver Outras Pesquisas
            </Button>
            <Button 
              size="large"
              icon={<HomeOutlined />}
              onClick={() => navigate('/')}
            >
              Voltar ao Início
            </Button>
          </Space>
        </Space>
      </Card>
    </div>
  );
};

export default SubmissionConfirmation;
